import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { GameDef, PracticeQuestion } from '@/sanskrit/lib/practice/generators'

import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { LoaderCircle } from 'lucide-react'
import { Seo } from '@/sanskrit/components/generals/seo'
import { Layout } from '@/sanskrit/components/generals/layout'
import { useAppState, useT } from '@/sanskrit/components/providers/app-state'
import { GAMES, generateSet, getGame } from '@/sanskrit/lib/practice/generators'
import { getBank, setQuestions } from '@/sanskrit/lib/practice/bank'
import { useSanskritSets } from '@/sanskrit/lib/practice/set-progress'
import { parseTopics, practiceHref, topicsForGame } from '@/sanskrit/lib/practice/topics'
import { mulberry32 } from '@/sanskrit/lib/random'
import PracticeSession from '@/sanskrit/components/practice/practice-session'
import { SetPicker } from '@/sanskrit/components/practice/set-picker'
import { FREE_SETS, SANSKRIT_FREE_GAMES } from '@/lib/plans'
import { useSession } from '@/lib/client/session'
import { SanskritGate, SanskritPremiumNotice } from '@/components/plans/sanskrit-gate'

interface GamePageProps {
    gameId: string
}

const levelOf = (raw: unknown, fallback: number): number => {
    const n = Number(Array.isArray(raw) ? raw[0] : raw)

    return n === 1 || n === 2 || n === 3 ? n : fallback
}

/** A quick round of one game (from a root's page: `?root=`), not part of the numbered sets. */
const QuickRound: FC<{ game: GameDef, level: number, dhatuId: string }> = ({ game, level, dhatuId }): ReactNode => {
    const t = useT()
    const [questions, setQuestions] = useState<PracticeQuestion[] | null>(null)
    const [round, setRound] = useState(0)

    useEffect(() => {
        const rand = mulberry32((Date.now() + round * 7919) % 1e9)
        setQuestions(generateSet(r => game.generate(r, level, { dhatuId }), game.length, rand))
    }, [game, level, dhatuId, round])

    return questions ? (
        <PracticeSession
            key={round}
            questions={questions}
            title={game.title}
            sa={game.sa}
            gameId={game.id}
            levelLabel={t({ hi: `स्तर ${level}`, en: `Level ${level}` })}
            onRestart={() => setRound(r => r + 1)}
        />
    ) : (
        <p className='py-24 text-center text-muted-foreground'>{t({ hi: 'खेल तैयार हो रहा है...', en: 'Setting up the game…' })}</p>
    )
}

/** One numbered set: its questions in a new order, the score saved when it ends, then the next set. */
const SetRound: FC<{ game: GameDef, level: number, topics: string[], set: number }> = ({ game, level, topics, set }): ReactNode => {
    const t = useT()
    const [round, setRound] = useState(0)
    const [questions, setQuestionsState] = useState<PracticeQuestion[] | null>(null)
    const [sets, setSets] = useState(0)
    const [bankKey, setBankKey] = useState<string | null>(null)
    const { save } = useSanskritSets(bankKey)
    const topicKey = topics.join(',')

    useEffect(() => {
        setQuestionsState(null)

        const timer = window.setTimeout(() => {
            const chosen = topicKey ? topicKey.split(',') : []
            const bank = getBank(game, level, chosen)

            setBankKey(bank.key)
            setSets(bank.sets)
            setQuestionsState(setQuestions(bank, game, level, chosen, set, Math.floor(Math.random() * 2147483646) + round))
        }, 30)

        return () => window.clearTimeout(timer)
    }, [game, level, topicKey, set, round])

    if (!questions) {
        return (
            <div className='flex min-h-64 items-center justify-center gap-3 text-muted-foreground' role='status'>
                <LoaderCircle className='size-6 animate-spin text-saffron-strong' aria-hidden='true' />
                {t({ hi: 'अभ्यास तैयार हो रहे हैं…', en: 'Getting the exercises ready…' })}
            </div>
        )
    }

    if (questions.length === 0) {
        return <p className='rounded-2xl bg-muted p-6 text-center text-muted-foreground'>{t({ hi: 'यह सेट नहीं मिला।', en: 'This set does not exist.' })}</p>
    }

    const allSets = practiceHref(game.id, level, topics)

    return (
        <PracticeSession
            key={`${set}-${round}`}
            questions={questions}
            title={game.title}
            sa={game.sa}
            gameId={game.id}
            exitHref={allSets}
            exitLabel={{ hi: 'सभी सेट', en: 'All sets' }}
            levelLabel={t({ hi: `स्तर ${level} · सेट ${set}`, en: `Level ${level} · Set ${set}` })}
            onFinish={(correct, total) => save(set, correct, total)}
            onRestart={() => setRound(r => r + 1)}
            next={set < sets ? { href: practiceHref(game.id, level, topics, set + 1), label: { hi: `सेट ${set + 1}`, en: `Set ${set + 1}` } } : undefined}
        />
    )
}

const GamePage: FC<GamePageProps> = ({ gameId }): ReactNode => {
    const router = useRouter()
    const { progress, hydrated } = useAppState()
    const { access, loading } = useSession()
    const game = getGame(gameId)
    const free = SANSKRIT_FREE_GAMES.includes(gameId)
    const ready = router.isReady && hydrated
    const level = levelOf(router.query.level, hydrated ? progress.games[gameId]?.level ?? 1 : 1)
    const topicText = typeof router.query.topic === 'string' ? router.query.topic : ''
    const topics = useMemo(() => (game ? topicsForGame(game.id, parseTopics(topicText)) : []), [game, topicText])
    const set = Number(router.query.set)
    const dhatuId = typeof router.query.root === 'string' ? router.query.root : undefined
    // Free games: the first FREE_SETS sets without Premium, as in the Prakrit course.
    const lockedFrom = access.sanskrit ? null : FREE_SETS + 1
    const setLocked = !loading && lockedFrom !== null && Number.isInteger(set) && set >= lockedFrom

    if (!game) return null

    return (
        <Layout>
            <Seo title={`${game.title.en} (${game.sa})`} description={`${game.desc.en} Up to 500 exercises in numbered sets of 10, by level and topic.`} />
            <SanskritGate free={free} what='This game'>
                {!ready ? (
                    <div className='flex min-h-64 items-center justify-center' role='status'>
                        <LoaderCircle className='size-6 animate-spin text-saffron-strong' aria-hidden='true' />
                    </div>
                ) : dhatuId ? (
                    <div className='mx-auto max-w-3xl px-4 pt-6 pb-16 sm:px-6'>
                        <QuickRound game={game} level={level} dhatuId={dhatuId} />
                    </div>
                ) : Number.isInteger(set) && set >= 1 ? (
                    setLocked ? (
                        <SanskritPremiumNotice what={`Set ${set} of this game`} />
                    ) : (
                        <div className='mx-auto max-w-3xl px-4 pt-6 pb-16 sm:px-6'>
                            <SetRound game={game} level={level} topics={topics} set={set} />
                        </div>
                    )
                ) : (
                    <SetPicker game={game} level={level} topics={topics} lockedFrom={lockedFrom} />
                )}
            </SanskritGate>
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => ({
    paths: GAMES.map(g => ({ params: { gameId: g.id } })),
    fallback: false
})

export const getStaticProps: GetStaticProps<GamePageProps> = async ({ params }) => {
    const gameId = String(params?.gameId ?? '')
    if (!getGame(gameId)) return { notFound: true }
    return { props: { gameId } }
}

export default GamePage
